/** Compact right-centered appearance dialog for mode + accent selection. */
import { useEffect, useId, useRef } from 'react';
import { Monitor, Moon, Sun, X } from 'lucide-react';
import {
  ACCENT_IDS,
  type AccentId,
  type ThemeModePreference,
  useTheme,
} from '../context/ThemeContext';

const MODE_OPTIONS: ReadonlyArray<{
  id: ThemeModePreference;
  label: string;
  icon: typeof Sun;
}> = [
  { id: 'light', label: 'Light', icon: Sun },
  { id: 'dark', label: 'Dark', icon: Moon },
  { id: 'system', label: 'System', icon: Monitor },
];

const ACCENT_LABELS: Record<AccentId, string> = {
  orange: 'Orange',
  green: 'Green',
  blue: 'Blue',
  violet: 'Violet',
  rose: 'Rose',
  cyan: 'Cyan',
};

type AppearanceDrawerProps = {
  readonly open: boolean;
  readonly onClose: () => void;
};

export function AppearanceDrawer({ open, onClose }: AppearanceDrawerProps) {
  const { mode, resolvedTheme, accent, setMode, setAccent } = useTheme();
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;

    const previous = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }

      if (event.key !== 'Tab' || !panelRef.current) return;
      const focusable = panelRef.current.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      previous?.focus?.();
    };
  }, [open, onClose]);

  if (!open) return null;

  const pairLabel = `${resolvedTheme === 'light' ? 'Light' : 'Dark'} · ${ACCENT_LABELS[accent]}`;

  return (
    <div className="appearance-dialog-root" role="presentation">
      <button
        type="button"
        className="appearance-dialog-overlay"
        aria-label="Close appearance settings"
        onClick={onClose}
      />
      <div
        id="appearance-dialog"
        ref={panelRef}
        className="appearance-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <header className="appearance-dialog-header">
          <div className="appearance-dialog-heading">
            <p className="appearance-dialog-kicker">Appearance</p>
            <h2 id={titleId} className="appearance-dialog-title">
              Look & feel
            </h2>
            <p className="appearance-dialog-pair">{pairLabel}</p>
          </div>
          <button
            ref={closeRef}
            type="button"
            className="appearance-dialog-close"
            aria-label="Close appearance settings"
            onClick={onClose}
          >
            <X size={16} strokeWidth={2.25} />
          </button>
        </header>

        <section className="appearance-dialog-section" aria-label="Theme mode">
          <h3 className="appearance-dialog-section-title">Theme</h3>
          <div className="appearance-mode-segment" role="group" aria-label="Theme mode">
            {MODE_OPTIONS.map(({ id, label, icon: Icon }) => {
              const selected = mode === id;
              return (
                <button
                  key={id}
                  type="button"
                  className={`appearance-mode-option${selected ? ' is-selected' : ''}`}
                  aria-pressed={selected}
                  onClick={() => setMode(id)}
                >
                  <Icon size={15} aria-hidden />
                  <span>{label}</span>
                </button>
              );
            })}
          </div>
        </section>

        <section className="appearance-dialog-section" aria-label="Accent color">
          <h3 className="appearance-dialog-section-title">Color</h3>
          <p className="appearance-dialog-hint">
            Accent for {resolvedTheme === 'light' ? 'light' : 'dark'} mode — remembered separately.
          </p>
          <div className="appearance-swatch-grid">
            {ACCENT_IDS.map((id) => {
              const selected = accent === id;
              return (
                <button
                  key={id}
                  type="button"
                  className={`appearance-swatch${selected ? ' is-selected' : ''}`}
                  data-accent-preview={id}
                  aria-label={`${ACCENT_LABELS[id]} accent`}
                  aria-pressed={selected}
                  onClick={() => setAccent(id)}
                >
                  <span className="appearance-swatch-chip" aria-hidden />
                  <span className="appearance-swatch-label">{ACCENT_LABELS[id]}</span>
                </button>
              );
            })}
          </div>
        </section>
      </div>
    </div>
  );
}
